import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { getDashboard, getFilters, getSubclasses, getMeta, getSnapshots, getVelocity, downloadDashboardExport, downloadItemsExport } from "@/lib/api";
import { fmtNum, fmtMoney, fmtMoneyShort } from "@/lib/format";
import { statusMeta } from "@/lib/statusMeta";
import KpiCard from "@/components/KpiCard";
import FilterBar from "@/components/FilterBar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronRight, ChevronDown, Upload, Boxes, PackageX, AlertTriangle, TrendingDown, Layers, CircleSlash, ShoppingCart, Truck, ArrowRight, Download } from "lucide-react";

const EMPTY = { warehouse: "", class: "", subclass: "", stocking_type: "", item_classification: "", pref_supp: "", status: "" };

export default function Dashboard() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY);
  const [exporting, setExporting] = useState(false);
  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters(EMPTY);

  const doExport = async () => {
    setExporting(true);
    try { await downloadDashboardExport(cleaned(filters)); toast.success("Dashboard exported to Excel"); }
    catch { toast.error("Export failed, please try again"); }
    finally { setExporting(false); }
  };

  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: getMeta });
  const { data: options } = useQuery({ queryKey: ["filters"], queryFn: getFilters, enabled: !!meta?.has_data });
  const { data: snapshots } = useQuery({ queryKey: ["snapshots"], queryFn: getSnapshots, enabled: !!meta?.has_data });
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", filters],
    queryFn: () => getDashboard(cleaned(filters)),
    enabled: !!meta?.has_data,
  });

  const snaps = snapshots || [];
  const [baseDate, setBaseDate] = useState("");
  const [toDate, setToDate] = useState("");
  React.useEffect(() => {
    if (snaps.length && !toDate) {
      setToDate(snaps[snaps.length - 1].date);
      setBaseDate(snaps[Math.max(0, snaps.length - 2)].date);
    }
  }, [snaps.length]);

  if (meta && !meta.has_data) return <EmptyState navigate={navigate} />;

  const anyFilter = Object.values(filters).some(Boolean);
  const base = snaps.find((s) => s.date === baseDate);
  const cur = snaps.find((s) => s.date === toDate);
  const canCompare = !anyFilter && base && cur && base !== cur;
  const deltaFor = (key) => (canCompare && key ? (cur[key] ?? 0) - (base[key] ?? 0) : null);
  const pctFor = (key) => (canCompare && key && base[key] ? (((cur[key] ?? 0) - base[key]) / Math.abs(base[key])) * 100 : null);

  const k = data?.kpis || {};
  const tiles = [
    { label: "Total Inventory", count: k.item_count, value: k.soh_value, vl: "SOH value", accent: "#0f172a", icon: Boxes, snap: "unique_items", goodWhenDown: false, go: () => drill(navigate, filters, {}) },
    { label: "Stock Outs", count: k.STOCK_OUT_count, value: k.STOCK_OUT_rec || k.STOCK_OUT_soh, vl: "exposure", accent: "#dc2626", icon: PackageX, snap: "stockout_count", goodWhenDown: true, go: () => drill(navigate, filters, { status: "STOCK_OUT" }) },
    { label: "Critical", count: k.CRITICAL_count, value: k.CRITICAL_soh, vl: "SOH value", accent: "#e11d48", icon: AlertTriangle, snap: "critical_count", goodWhenDown: true, go: () => drill(navigate, filters, { status: "CRITICAL" }) },
    { label: "Potential Stockouts", count: k.POTENTIAL_STOCKOUT_count, value: k.POTENTIAL_STOCKOUT_soh, vl: "SOH value", accent: "#d97706", icon: TrendingDown, snap: "potential_count", goodWhenDown: true, go: () => drill(navigate, filters, { status: "POTENTIAL_STOCKOUT" }) },
    { label: "Overstocked", count: k.OVERSTOCKED_count, value: k.overstock_excess_value, vl: "excess value", accent: "#7c3aed", icon: Layers, snap: "overstock_count", goodWhenDown: true, go: () => drill(navigate, filters, { status: "OVERSTOCKED" }) },
    { label: "Non-Movers", count: k.NON_MOVER_count, value: k.NON_MOVER_soh, vl: "SOH value", accent: "#64748b", icon: CircleSlash, snap: "nonmover_count", goodWhenDown: true, go: () => drill(navigate, filters, { status: "NON_MOVER" }) },
    { label: "PO Required", count: k.po_required_count, value: k.recommended_po_value, vl: "recommended PO", accent: "#2563eb", icon: ShoppingCart, snap: "po_required_count", goodWhenDown: true, go: () => drill(navigate, filters, { po: "true" }) },
    { label: "Existing Supply Pipeline", count: k.pipeline_count, value: k.pipeline_value, vl: "incoming value", accent: "#0d9488", icon: Truck, snap: null, goodWhenDown: false, go: () => drill(navigate, filters, {}) },
  ];

  const fmtSnapDate = (d) => new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  const valDelta = canCompare ? (cur.total_soh_value ?? 0) - (base.total_soh_value ?? 0) : null;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Operational Overview</div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Inventory Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Consolidated view across all warehouses · counts use distinct Item Codes</p>
        </div>
        <div className="flex items-start gap-3 flex-wrap justify-end">
          <button onClick={doExport} disabled={exporting} data-testid="dashboard-export-btn"
            className="h-9 px-3 rounded-md text-white text-[13px] font-medium flex items-center gap-1.5 shrink-0 disabled:opacity-60"
            style={{ background: "var(--kcsa-red)" }}>
            <Download className="w-4 h-4" /> {exporting ? "Exporting…" : "Export to Excel"}
          </button>
          <SnapshotComparator snaps={snaps} baseDate={baseDate} toDate={toDate} setBaseDate={setBaseDate} setToDate={setToDate} fmtSnapDate={fmtSnapDate} valDelta={valDelta} anyFilter={anyFilter} />
        </div>
      </div>

      <div className="kcard p-4">
        <FilterBar filters={filters} setFilter={setFilter} clearFilters={clearFilters} options={options} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tiles.map((t) => (
          <KpiCard key={t.label} label={t.label} count={t.count} value={t.value} valueLabel={t.vl} accent={t.accent} icon={t.icon}
            delta={deltaFor(t.snap)} deltaPct={pctFor(t.snap)} goodWhenDown={t.goodWhenDown}
            onClick={t.go} testId={`kpi-${t.label.toLowerCase().replace(/ /g, "-")}`} />
        ))}
      </div>

      {/* Warehouse analysis */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <div className="kcard p-4 xl:col-span-2">
          <div className="label-eyebrow mb-3">SOH Value by Warehouse</div>
          <WarehouseDonut rows={data?.warehouses} onSlice={(w) => drill(navigate, filters, { warehouse: w })} />
          <div className="border-t border-[var(--line)] mt-4 pt-4">
            <div className="label-eyebrow mb-3">Capital by Consumption Velocity</div>
            <VelocityBreakdown filters={filters} />
          </div>
        </div>
        <div className="kcard p-0 xl:col-span-3 overflow-hidden">
          <div className="label-eyebrow px-4 pt-4 pb-2">Warehouse Analysis</div>
          <AnalysisTable rows={data?.warehouses} nameKey="warehouse" onRow={(r) => drill(navigate, filters, { warehouse: r.warehouse })} loading={isLoading} />
        </div>
      </div>

      {/* Class analysis */}
      <div className="kcard p-0 overflow-hidden">
        <div className="label-eyebrow px-4 pt-4 pb-2">Class Analysis · drill Class → Subclass → Item</div>
        <ClassTable rows={data?.classes} filters={filters} navigate={navigate} />
      </div>
    </div>
  );
}

const cleaned = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v));
function drill(navigate, filters, extra) {
  const q = { ...cleaned(filters), ...extra };
  const qs = new URLSearchParams(q).toString();
  navigate(`/advisor?${qs}`);
}

const METRIC_COLS = [
  ["Items", "item_count", fmtNum, "neutral"],
  ["SOH Value", "soh_value", fmtMoneyShort, "neutral"],
  ["Stockout", "STOCK_OUT_count", fmtNum, "risk"],
  ["Critical", "CRITICAL_count", fmtNum, "risk"],
  ["Pot.", "POTENTIAL_STOCKOUT_count", fmtNum, "risk"],
  ["Overstk", "OVERSTOCKED_count", fmtNum, "risk"],
  ["Excess $", "overstock_excess_value", fmtMoneyShort, "risk"],
  ["Non-Mov", "NON_MOVER_count", fmtNum, "risk"],
  ["Non-Mov $", "NON_MOVER_soh", fmtMoneyShort, "risk"],
  ["PO Req", "po_required_count", fmtNum, "risk"],
];

const colMax = (rows, key) => Math.max(0, ...(rows || []).map((r) => Number(r[key]) || 0));
const Pill = ({ cls, children }) => (
  <span className={`inline-block min-w-[56px] text-center px-2.5 py-1 rounded-full num text-[12px] font-medium ${cls}`}>{children}</span>
);
function riskTone(n, max) {
  n = Number(n) || 0;
  if (n <= 0) return "bg-emerald-50 text-emerald-700";
  const ratio = max > 0 ? n / max : 0;
  if (ratio < 0.25) return "bg-amber-50 text-amber-700";
  return "bg-rose-50 text-rose-700";
}
function MetricCells({ row, maxes, muted }) {
  return METRIC_COLS.map(([h, key, fmt, type]) => (
    <td key={h} className="px-2 py-2 text-center">
      <Pill cls={type === "neutral" ? (muted ? "bg-slate-50 text-slate-500" : "bg-slate-100 text-slate-700") : riskTone(row[key], maxes[key])}>{fmt(row[key])}</Pill>
    </td>
  ));
}
const riskMaxes = (rows) => {
  const m = {};
  METRIC_COLS.forEach(([, k, , t]) => { if (t === "risk") m[k] = colMax(rows, k); });
  return m;
};

function AnalysisTable({ rows, nameKey, onRow, loading }) {
  const maxes = riskMaxes(rows);
  return (
    <div className="overflow-x-auto slim">
      <table className="w-full text-[13px]">
        <thead className="bg-[var(--surface)]">
          <tr>
            <th className="thead-cell text-left px-4 py-2 sticky left-0 bg-[var(--surface)]">{nameKey === "warehouse" ? "Warehouse" : "Class"}</th>
            {METRIC_COLS.map(([h]) => <th key={h} className="thead-cell text-center px-2 py-2">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={11} className="px-4 py-6 text-center text-slate-400">Loading…</td></tr>}
          {(rows || []).map((r, i) => (
            <tr key={i} className="border-t border-[var(--line)] hover:bg-slate-50 cursor-pointer" onClick={() => onRow(r)} data-testid={`wh-row-${r[nameKey]}`}>
              <td className="px-4 py-2 font-medium text-slate-800 sticky left-0 bg-white">{r[nameKey]}</td>
              <MetricCells row={r} maxes={maxes} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ClassTable({ rows, filters, navigate }) {
  const [open, setOpen] = useState(null);
  const exportClass = async (klass, subclass) => {
    try {
      await downloadItemsExport({ ...cleaned(filters), class: klass, ...(subclass ? { subclass } : {}) });
      toast.success(`Exporting ${subclass || klass} line items…`);
    } catch { toast.error("Export failed, please try again"); }
  };
  const { data: subs } = useQuery({
    queryKey: ["subclasses", open, filters.warehouse],
    queryFn: () => getSubclasses({ class: open, warehouse: filters.warehouse || undefined }),
    enabled: !!open,
  });
  const maxes = riskMaxes(rows);
  const subMaxes = riskMaxes(subs);
  return (
    <div className="overflow-x-auto slim max-h-[560px]">
      <table className="w-full text-[13px]">
        <thead className="bg-[var(--surface)] sticky top-0 z-10">
          <tr>
            <th className="thead-cell text-left px-4 py-2">Class</th>
            {METRIC_COLS.map(([h]) => <th key={h} className="thead-cell text-center px-2 py-2">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r, i) => (
            <React.Fragment key={i}>
              <tr className="border-t border-[var(--line)] hover:bg-slate-50">
                <td className="px-4 py-2 font-medium text-slate-800">
                  <div className="flex items-center gap-2">
                    <button className="flex items-center gap-1.5 text-left" onClick={() => setOpen(open === r.klass ? null : r.klass)} data-testid={`class-row-${i}`}>
                      {open === r.klass ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      {r.klass || "–"}
                    </button>
                    <button onClick={() => exportClass(r.klass)} title="Export this class's line items to Excel" data-testid={`class-export-${i}`}
                      className="text-slate-400 hover:text-[var(--kcsa-red)] transition-colors"><Download className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
                <MetricCells row={r} maxes={maxes} />
              </tr>
              {open === r.klass && (subs || []).map((s, j) => (
                <tr key={`s${j}`} className="bg-slate-50/60 border-t border-[var(--line)] cursor-pointer hover:bg-slate-100"
                  onClick={() => { const q = new URLSearchParams({ ...cleaned(filters), class: r.klass, subclass: s.subclass }).toString(); navigate(`/advisor?${q}`); }}>
                  <td className="pl-10 pr-4 py-1.5 text-slate-600">
                    <div className="flex items-center gap-2">
                      <span>{s.subclass || "–"}</span>
                      <button onClick={(e) => { e.stopPropagation(); exportClass(r.klass, s.subclass); }} title="Export this subclass's line items to Excel"
                        className="text-slate-400 hover:text-[var(--kcsa-red)] transition-colors"><Download className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                  <MetricCells row={s} maxes={subMaxes} muted />
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const PALETTE = ["#e5323f", "#f97316", "#f59e0b", "#eab308", "#84cc16", "#10b981", "#06b6d4", "#3b82f6", "#6366f1", "#8b5cf6", "#ec4899"];

const VELO_META = {
  VERY_HIGH: { label: "Very High", sub: "≥ $1M/yr", color: "#059669" },
  HIGH: { label: "High", sub: "$100k–1M/yr", color: "#0d9488" },
  MEDIUM: { label: "Medium", sub: "$10k–100k/yr", color: "#2563eb" },
  LOW: { label: "Low", sub: "< $10k/yr", color: "#d97706" },
  DEAD: { label: "Non-Mover", sub: "no demand", color: "#e11d48" },
};

function VelocityBreakdown({ filters }) {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["velocity", filters], queryFn: () => getVelocity(cleaned(filters)) });
  const rows = data || [];
  const maxVal = Math.max(1, ...rows.map((r) => r.value));
  if (!rows.length) return <div className="text-slate-400 text-sm py-4">No data</div>;
  const go = (band) => navigate(`/advisor?${new URLSearchParams({ ...cleaned(filters), velocity: band }).toString()}`);
  return (
    <div className="space-y-2.5">
      {rows.map((r) => {
        const m = VELO_META[r.band];
        return (
          <button key={r.band} onClick={() => go(r.band)} className="w-full flex items-center gap-3 rounded-md px-1 py-0.5 hover:bg-slate-50 transition-colors" data-testid={`velocity-${r.band}`}>
            <div className="w-[108px] shrink-0 text-left">
              <div className="text-[12.5px] font-medium text-slate-700 leading-none">{m.label}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">{m.sub}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="h-5 rounded bg-slate-100 overflow-hidden">
                <div className="h-full rounded transition-all" style={{ width: `${(r.value / maxVal) * 100}%`, background: m.color, minWidth: r.value > 0 ? 3 : 0 }} />
              </div>
            </div>
            <div className="w-[88px] shrink-0 text-right">
              <div className="num text-[12.5px] font-semibold text-slate-800">{fmtMoneyShort(r.value)}</div>
              <div className="num text-[10px] text-slate-400">{fmtNum(r.count)} lines</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function WarehouseDonut({ rows, onSlice }) {
  const data = (rows || []).filter((r) => r.soh_value > 0).map((r) => ({ name: r.warehouse, value: r.soh_value }));
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  if (!data.length) return <div className="h-[220px] grid place-items-center text-slate-400 text-sm">No value to display</div>;
  return (
    <div className="flex items-center gap-5 flex-wrap">
      <div className="relative shrink-0" style={{ width: 200, height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={62} outerRadius={92} paddingAngle={2} stroke="#fff" strokeWidth={2}
              onClick={(d) => d && onSlice(d.name)} style={{ cursor: "pointer", outline: "none" }}>
              {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
            </Pie>
            <Tooltip formatter={(v) => fmtMoney(v)} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <div className="text-center">
            <div className="label-eyebrow">Total SOH</div>
            <div className="num font-bold text-slate-900 text-[15px]">{fmtMoneyShort(total)}</div>
          </div>
        </div>
      </div>
      <div className="flex-1 min-w-[170px] space-y-0.5 max-h-[210px] overflow-y-auto slim">
        {data.map((d, i) => (
          <button key={d.name} onClick={() => onSlice(d.name)} className="w-full flex items-center gap-2 py-1 px-1.5 rounded hover:bg-slate-50 text-left">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: PALETTE[i % PALETTE.length] }} />
            <span className="text-[12.5px] text-slate-700 flex-1 truncate">{d.name}</span>
            <span className="num text-[12px] font-medium text-slate-800">{fmtMoneyShort(d.value)}</span>
            <span className="num text-[11px] text-slate-400 w-10 text-right">{((d.value / total) * 100).toFixed(1)}%</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function SnapshotComparator({ snaps, baseDate, toDate, setBaseDate, setToDate, fmtSnapDate, valDelta, anyFilter }) {
  if (!snaps.length) return null;
  const single = snaps.length < 2;
  const up = valDelta > 0;
  return (
    <div className="kcard p-3 min-w-[320px]" data-testid="snapshot-comparator">
      <div className="label-eyebrow mb-2">Inventory Snapshot · change between uploads</div>
      {single ? (
        <div className="text-[12px] text-slate-400">Upload another daily file to compare snapshots.</div>
      ) : (
        <div className="flex items-center gap-2">
          <Select value={baseDate} onValueChange={setBaseDate}>
            <SelectTrigger className="h-8 text-[12px] w-[130px]" data-testid="snap-from"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">{snaps.map((s) => <SelectItem key={s.date} value={s.date} className="text-[12px]">{fmtSnapDate(s.date)}</SelectItem>)}</SelectContent>
          </Select>
          <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
          <Select value={toDate} onValueChange={setToDate}>
            <SelectTrigger className="h-8 text-[12px] w-[130px]" data-testid="snap-to"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">{snaps.map((s) => <SelectItem key={s.date} value={s.date} className="text-[12px]">{fmtSnapDate(s.date)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {!single && (
        <div className="mt-2 flex items-baseline gap-2">
          <span className="label-eyebrow">SOH value</span>
          {anyFilter ? (
            <span className="text-[12px] text-slate-400">clear filters to see trend</span>
          ) : (
            <span className={`text-[15px] font-bold num ${valDelta === 0 ? "text-slate-500" : up ? "text-emerald-600" : "text-rose-600"}`}>
              {valDelta === 0 ? "No change" : `${up ? "▲" : "▼"} ${fmtMoneyShort(Math.abs(valDelta))}`}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState({ navigate }) {
  return (
    <div className="p-6">
      <div className="kcard p-12 text-center max-w-lg mx-auto mt-16">
        <Upload className="w-10 h-10 mx-auto text-slate-300" />
        <h2 className="text-lg font-bold text-slate-800 mt-4">No inventory data yet</h2>
        <p className="text-sm text-slate-500 mt-1">Upload your daily inventory Excel extract to populate the dashboard.</p>
        <button onClick={() => navigate("/upload")} data-testid="goto-upload" className="mt-5 h-10 px-5 rounded-md text-white text-sm font-medium" style={{ background: "var(--kcsa-red)" }}>Go to Data Upload</button>
      </div>
    </div>
  );
}
